import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { API_URL, TOKEN } from "../../../variables/constants";

const AddValeurPointIP = ({
  show,
  handleClose,
  editMode = false,
  selectedItem = null,
  items = [],
  onAdd,
  onUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    trancheTauxIpStr: "",
    trancheAgeBlesseStr: "",
    valeur: "",
  });

  const toArrayOfInts = (str) =>
    str
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n));

  const normalizeArray = (arr) => [...arr].map((n) => Number(n)).sort((a, b) => a - b);

  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  };

  useEffect(() => {
    if (editMode && selectedItem) {
      setFormData({
        trancheTauxIpStr: (selectedItem.trancheTauxIp || []).join(", "),
        trancheAgeBlesseStr: (selectedItem.trancheAgeBlesse || []).join(", "),
        valeur:
          selectedItem.valeur !== null && selectedItem.valeur !== undefined
            ? String(selectedItem.valeur)
            : "",
      });
    } else {
      setFormData({ trancheTauxIpStr: "", trancheAgeBlesseStr: "", valeur: "" });
    }
  }, [editMode, selectedItem, show]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  const validate = (tauxArr, ageArr, valeur) => {
    if (!tauxArr.length) {
      toast.warning("Veuillez saisir au moins un taux IP.");
      return false;
    }
    if (!ageArr.length) {
      toast.warning("Veuillez saisir au moins une tranche d'âge blessé.");
      return false;
    }
    if (tauxArr.some((n) => n < 0) || ageArr.some((n) => n < 0)) {
      toast.warning("Les valeurs doivent être des entiers positifs ou nuls.");
      return false;
    }
    if (!Number.isFinite(valeur)) {
      toast.warning("Veuillez saisir une valeur numérique pour le point IP.");
      return false;
    }
    if (valeur <= 0) {
      toast.warning("La valeur du point IP doit être strictement positive.");
      return false;
    }
    return true;
  };

  // Doublon sur combinaison (trancheTauxIp, trancheAgeBlesse) en création
  const isDuplicateOnCreate = (tauxArr, ageArr) => {
    const nTaux = normalizeArray(tauxArr);
    const nAge = normalizeArray(ageArr);
    return items?.some((it) => {
      const itTaux = normalizeArray(it.trancheTauxIp || []);
      const itAge = normalizeArray(it.trancheAgeBlesse || []);
      return arraysEqual(itTaux, nTaux) && arraysEqual(itAge, nAge);
    });
  };

  const resetAndClose = () => {
    setFormData({ trancheTauxIpStr: "", trancheAgeBlesseStr: "", valeur: "" });
    handleClose && handleClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const trancheTauxIp = toArrayOfInts(formData.trancheTauxIpStr);
    const trancheAgeBlesse = toArrayOfInts(formData.trancheAgeBlesseStr);

    const valeurNumber = Number(formData.valeur);

    if (!validate(trancheTauxIp, trancheAgeBlesse, valeurNumber)) return;
    if (!editMode && isDuplicateOnCreate(trancheTauxIp, trancheAgeBlesse)) {
      toast.error("Cette combinaison de tranches existe déjà.");
      return;
    }

    setLoading(true);

    const base = {
      trancheTauxIp,
      trancheAgeBlesse,
      valeur: valeurNumber,
    };

    const headers = {
      Authorization: `Bearer ${TOKEN}`,
      "Content-Type": "application/json",
    };

    try {
      if (editMode) {
        const payload = { ...selectedItem, ...base }; // inclut valeurPointIpId
        const res = await axios.put(`${API_URL}/api/valeur-point-ip`, payload, { headers });
        toast.success("Valeur Point IP modifiée avec succès.");
        onUpdate && onUpdate(res.data);
      } else {
        const res = await axios.post(`${API_URL}/api/valeur-point-ip`, base, { headers });
        toast.success("Valeur Point IP ajoutée avec succès.");
        onAdd && onAdd(res.data);
      }
      resetAndClose();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Body>
        <button type="button" className="btn-close btn-primary" onClick={handleClose} />
        <div className="text-center mb-4">
          <h4>{editMode ? "Modifier" : "Ajouter"} une Valeur Point IP</h4>
          <p className="address-subtitle">
            {editMode ? "Mettre à jour" : "Créer"} une configuration de tranches
          </p>
        </div>

        <form className="row g-3" onSubmit={onSubmit}>
          <div className="col-md-6">
            <label className="form-label">Tranches Taux IP (séparées par des virgules)</label>
            <input
              type="text"
              className="form-control"
              name="trancheTauxIpStr"
              value={formData.trancheTauxIpStr}
              onChange={onChange}
              placeholder="ex: 10, 20, 30"
              required
            />
            <small className="text-muted">Ex: 10, 15, 35</small>
          </div>

          <div className="col-md-6">
            <label className="form-label">Tranches Âge Blessé (séparées par des virgules)</label>
            <input
              type="text"
              className="form-control"
              name="trancheAgeBlesseStr"
              value={formData.trancheAgeBlesseStr}
              onChange={onChange}
              placeholder="ex: 18, 25, 35"
              required
            />
            <small className="text-muted">Ex: 18, 25, 35</small>
          </div>

          <div className="col-md-6">
            <label className="form-label">Valeur du point IP</label>
            <input
              type="number"
              className="form-control"
              name="valeur"
              value={formData.valeur}
              onChange={onChange}
              min="0"
              step="0.01"
              required
            />
          </div>

          <div className="col-12 d-flex gap-2 mt-3">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={handleClose}>
              Annuler
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddValeurPointIP;
